/**
 * verifySubscription — logic behind the OfflineLicenseBanner "Verify Now" button.
 *
 * Re-verifying Pro entitlement requires a live OIDC access token. When the
 * session has lapsed, `getAccessToken()` returns null and the old handler
 * still fired fetchSubscription/refreshEntitlementToken/validateCache — all of
 * which fail silently without a token, so the button did nothing visible.
 *
 * This routine checks for the token first and short-circuits to 'needs-signin'
 * so the caller can trigger an interactive re-sign-in instead of a silent no-op.
 *
 * Pure and dependency-injected so it can be unit tested without Electron/React.
 */

/** Outcome of a "Verify Now" attempt, used by the UI to decide what to show. */
export type VerifyOutcome =
  /** Entitlement re-minted successfully — banner should clear. */
  | 'verified'
  /** No usable access token — the user must re-authenticate. */
  | 'needs-signin'
  /** Had a token but the refresh failed (network/server). */
  | 'error';

export interface VerifyDeps {
  /** Returns the current OIDC access token, or null if the session has lapsed. */
  getAccessToken: () => Promise<string | null>;
  /** Re-fetch subscription state from the backend. */
  fetchSubscription: () => Promise<void>;
  /** Re-mint + store the signed entitlement token. Returns false on failure. */
  refreshEntitlementToken: () => Promise<boolean>;
  /** Re-validate the offline cache so the banner reflects current state. */
  validateCache: () => Promise<unknown>;
}

export async function verifySubscription(deps: VerifyDeps): Promise<VerifyOutcome> {
  let token: string | null = null;
  try {
    token = await deps.getAccessToken();
  } catch {
    // IPC failure looking up the token — treat as a lapsed session.
    return 'needs-signin';
  }

  if (!token) {
    // No live session: firing the online calls can only fail. Prompt re-auth.
    return 'needs-signin';
  }

  // Guard the online calls: a rejection here must NOT escape into the
  // fire-and-forget click handler (where it would surface as a crash dialog or
  // a silent no-op). Map an auth failure to re-sign-in, anything else to a
  // graceful 'error' the UI can toast.
  try {
    await deps.fetchSubscription();
    const refreshed = await deps.refreshEntitlementToken();
    await deps.validateCache();
    return refreshed ? 'verified' : 'error';
  } catch (err) {
    const message = err instanceof Error ? err.message : '';
    return message.includes('Not authenticated') ? 'needs-signin' : 'error';
  }
}
